import { FormLabel, Grid } from '@material-ui/core';
import { FormikAddressPicker, FormikAutocompletePx, FormikTextFieldPx } from '@webapps/reactjs-tech-components';
import { useStyles } from '@webapps/styles';
import { componentMap } from 'config/forms';
import { useFormik } from 'formik';
import * as yup from 'yup';
import React from 'react';


const FormMapper = ({ formConfig, info, onSubmit, className, gridProps = { spacing: 3 }, settings = { submitType: 'onBlur' } }) => {
  const { global } = useStyles();

  // Parse field path and set value in a nested object
  const setNestedValue = (obj, path, value) => {
    const parts = path.match(/([^\[\].]+)|\[(\d+)\]/g) || [];
    const hasArrayIndex = parts.some(part => part.startsWith('[') && part.endsWith(']'));
    let current = obj;

    if (hasArrayIndex) {
      const rootKey = parts[0];
      if (!current[rootKey]) {
        current[rootKey] = [];
      }
      current = current[rootKey];
    }

    for (let i = hasArrayIndex ? 1 : 0; i < parts.length; i++) {
      const part = parts[i];
      if (part.startsWith('[') && part.endsWith(']')) {
        const index = parseInt(part.slice(1, -1), 10);
        while (current.length <= index) {
          current.push({});
        }
        current = current[index];
      } else if (i === parts.length - 1) {
        current[part] = value;
      } else {
        if (!current[part]) {
          current[part] = {};
        }
        current = current[part];
      }
    }
  };

  // Build initialValues with nested structure
  const initialValues = {};
  formConfig.forEach((field) => {
    if (field.type === 'custom') {
      // Handle dynamic fields like businessContractLengths
      const initialValue = field.getInitial ? field.getInitial(info) : info?.[field.name] || [{ year: 1, ageChange: false }];
      setNestedValue(initialValues, field.name || 'businessContractLengths', initialValue);
    } else {
      const initialValue = field.getInitial ? field.getInitial(info) : info?.[field.name] ?? null;
      if (field.type === 'addressNested') {
        setNestedValue(initialValues, field.name, initialValue && typeof initialValue === 'object'
          ? initialValue
          : { label: '', city: '', cityCode: '' });
      } else {
        setNestedValue(initialValues, field.name, initialValue);
      }
    }
  });

  // Build validation schema without nested structure
  const buildNestedSchema = (fields) => {
    const schemaObj = {};
    fields.forEach((field) => {
      if (field.validation && field.type !== 'custom') { // Exclude custom fields from schema
        let schema = yup.mixed();
        if (field.type === 'number') {
          schema = yup.number().typeError(field.validation.message);
          if (field.validation.min) schema = schema.min(field.validation.min, field.validation.message);
          if (field.validation.max) schema = schema.max(field.validation.max, field.validation.message);
        } else if (field.type === 'text' || field.type === 'phone') {
          schema = yup.string().nullable();
          if (field.validation.maxLength) schema = schema.max(field.validation.maxLength, field.validation.message);
          if (field.validation.regex) schema = schema.matches(field.validation.regex, field.validation.message);
          if (field.validation.requiredLength) schema = schema.length(field.validation.requiredLength, field.validation.message);
          if (field.validation.numeric) schema = schema.matches(/^\d+$/, { message: field.validation.message });
        } else {
          schema = yup.string().nullable();
        }
        if (field.required) schema = schema.required(field.validation.message);
        schemaObj[field.name] = schema;
      }
    });
    return yup.object().shape(schemaObj);
  };

  const validationSchema = buildNestedSchema(formConfig);

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      onSubmit(values);
    },
    validateOnMount: false,
  });


  // Access nested values, errors, and touched
  const getNestedValue = (obj, path) => {
    if (!obj || !path) return null;

    if (obj === formik.errors || obj === formik.touched) {
      return path in obj ? obj[path] : null;
    }

    const parts = path.match(/([^\[\].]+)|\[(\d+)\]/g) || [];
    let current = obj;
    for (let i = 0; i < parts.length; i++) {
      const part = parts[i];
      if (part.startsWith('[') && part.endsWith(']')) {
        const index = parseInt(part.slice(1, -1), 10);
        const arrayKey = parts[i - 1];
        if (!current || !current[arrayKey] || !Array.isArray(current[arrayKey]) || index >= current[arrayKey].length) {
          return null;
        }
        current = current[arrayKey][index];
      } else {
        if (!current || current[part] === undefined) {
          return null;
        }
        current = current[part];
      }
    }
    return current;
  };

  const errorFields = formConfig.reduce((acc, field) => {
    if (field.type === 'custom') {
      acc[field.name || 'businessContractLengths'] = false; // Custom field validation handled separately
    } else {
      const value = getNestedValue(formik.values, field.name);
      const error = getNestedValue(formik.errors, field.name);
      const touched = getNestedValue(formik.touched, field.name);
      const hasYupError = touched && error;
      const isInvalid = field.required && (value == null || value === '' || (typeof value === 'number' && value === 0));
      acc[field.name] = hasYupError || isInvalid;
    }
    return acc;
  }, {});

  const requiredVisibleFields = formConfig.filter(field => field.required && field.type !== 'none' && field.type !== 'custom');

  const invalidCount = Object.values(errorFields).filter(Boolean).length;
  const totalFields = requiredVisibleFields.length;
  const validCount = totalFields - invalidCount;

  const component = (
    <Grid container {...gridProps}>
      {formConfig.map((field, index) => {
        if (field.type === 'none') return null;
        const Component = field.type === 'custom' ? field.render : componentMap[field.type] || (() => null);
        const error = field.type !== 'custom' ? getNestedValue(formik.errors, field.name) || null : null;
        const touched = field.type !== 'custom' ? !!getNestedValue(formik.touched, field.name) : false;
        return (
          <Grid
            item
            xs={field.type === 'custom' ? 12 : 4} // Full width for custom fields
            key={field.name || index}
            className={field.type !== 'custom' && errorFields[field.name] ? global.formikTextFieldWhiteError : field.disabled ? global.formikTextFieldWhiteDisabled : global.formikTextFieldWhite}
          >
            <Component
              {...field}
              formik={formik}
              error={error}
              touched={touched}
              settings={settings}
            />
          </Grid>
        );
      })}
    </Grid>
  );

  return {
    component,
    formik,
    errorFields,
    validCount,
    invalidCount,
    totalFields,
  };
};

export default FormMapper;